import { useCallback, useEffect, useState } from "react";
import {
  createDraftMatter,
  deleteDraftMatter,
  fetchDraftMatter,
  fetchDraftMatters,
  updateDraftMatter,
} from "@/lib/api/drafts";
import { shouldAdvanceDraftMatterStatus } from "@/lib/draft-matter-status";
import type {
  CreateDraftMatterInput,
  DraftMatter,
  DraftMatterDetail,
  DraftMatterStatus,
  UpdateDraftMatterInput,
} from "@/types/drafting";

export function useDraftMatters() {
  const [matters, setMatters] = useState<DraftMatter[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const rows = await fetchDraftMatters();
      setMatters(rows);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load drafts");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const createMatter = useCallback(async (input: CreateDraftMatterInput) => {
    const created = await createDraftMatter(input);
    setMatters((prev) => [created, ...prev]);
    return created;
  }, []);

  const removeMatter = useCallback(async (id: string) => {
    await deleteDraftMatter(id);
    setMatters((prev) => prev.filter((m) => m.id !== id));
  }, []);

  return {
    matters,
    isLoading,
    error,
    refresh,
    createMatter,
    removeMatter,
  };
}

export function useDraftMatter(
  matterId: string | undefined,
  options?: { syncStepStatus?: DraftMatterStatus }
) {
  const [matter, setMatter] = useState<DraftMatterDetail | null>(null);
  const [isLoading, setIsLoading] = useState(Boolean(matterId));
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!matterId) return;
    setIsLoading(true);
    setError(null);
    try {
      const detail = await fetchDraftMatter(matterId);
      setMatter(detail);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load draft matter");
    } finally {
      setIsLoading(false);
    }
  }, [matterId]);

  const refreshSilently = useCallback(async () => {
    if (!matterId) return;
    try {
      const detail = await fetchDraftMatter(matterId);
      setMatter(detail);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load draft matter");
    }
  }, [matterId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    const targetStatus = options?.syncStepStatus;
    if (!matterId || !matter || !targetStatus) return;
    if (!shouldAdvanceDraftMatterStatus(matter.status, targetStatus)) return;

    let cancelled = false;
    void (async () => {
      try {
        const updated = await updateDraftMatter(matterId, { status: targetStatus });
        if (!cancelled) {
          setMatter((prev) => (prev ? { ...prev, ...updated } : prev));
        }
      } catch {
        // Non-blocking: step UI still works if status sync fails
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [matter, matter?.status, matterId, options?.syncStepStatus]);

  const saveMatter = useCallback(
    async (input: UpdateDraftMatterInput) => {
      if (!matterId) return null;
      const updated = await updateDraftMatter(matterId, input);
      setMatter((prev) => (prev ? { ...prev, ...updated } : prev));
      return updated;
    },
    [matterId]
  );

  const applyVersionContent = useCallback((contentJson: unknown, versionId?: string) => {
    setMatter((prev) => {
      if (!prev) return prev;
      const targetVersionId = versionId ?? prev.current_version_id;
      if (!targetVersionId || !prev.versions?.length) {
        return prev;
      }
      const versions = prev.versions.map((version) =>
        version.id === targetVersionId ? { ...version, content_json: contentJson } : version
      );
      return { ...prev, versions };
    });
  }, []);

  return {
    matter,
    isLoading,
    error,
    refresh,
    refreshSilently,
    saveMatter,
    applyVersionContent,
  };
}
